import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FolderLock,
  UploadCloud,
  Camera,
  Eye,
  Trash2,
  FileText,
  RefreshCw,
  HardDrive,
  CheckCircle,
  AlertTriangle,
  Archive,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { DocumentUploader } from '@/components/documents/DocumentUploader';
import { PhotoCaptureModal } from '@/components/documents/PhotoCaptureModal';
import { DocumentPreviewModal } from '@/components/documents/DocumentPreviewModal';
import { SoftVoidConfirmModal } from '@/components/common/SoftVoidConfirmModal';
import { PageHeader } from '@/components/common/PageHeader';
import { ActionTooltip } from '@/components/common/ActionTooltip';
import { YearSelector } from '@/components/reports/YearSelector';
import { KpiCardGroup } from '@/components/common/KpiCardGroup';
import { KpiCard } from '@/components/common/KpiCard';
import { FilterBar } from '@/components/common/FilterBar';
import { Combobox } from '@/components/common/Combobox';
import { documentService } from '@/services/documentService';
import { DocumentRecord } from '@/types/database';
import { formatDate, formatFileSize } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';

export const DocumentsPage: React.FC = () => {
  const { isOperations } = useAuth();
  const [documents, setDocuments] = useState<DocumentRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Year filter state (defaults to latest year with data)
  const [selectedYear, setSelectedYear] = useState<string>('2026');

  // Filters
  const [search, setSearch] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [entityFilter, setEntityFilter] = useState<string>('');

  // Modals
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
  const [showCameraModal, setShowCameraModal] = useState<boolean>(false);
  const [previewDoc, setPreviewDoc] = useState<DocumentRecord | null>(null);
  const [voidDoc, setVoidDoc] = useState<DocumentRecord | null>(null);

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const data = await documentService.getAllDocuments(500);
      setDocuments(data);
    } catch (err) {
      console.error('[DocumentsPage] Error loading documents:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  // Derive available years dynamically from document upload dates (created_at)
  const availableYears = useMemo(() => {
    const yearsSet = new Set<string>();
    documents.forEach((d) => {
      if (d.created_at) {
        const yr = d.created_at.substring(0, 4);
        if (/^\d{4}$/.test(yr)) yearsSet.add(yr);
      }
    });

    if (yearsSet.size === 0) {
      yearsSet.add('2026');
      yearsSet.add('2025');
      yearsSet.add('2024');
    }

    return Array.from(yearsSet).sort((a, b) => b.localeCompare(a));
  }, [documents]);

  // Synchronize default year if current selected is not in list
  useEffect(() => {
    if (availableYears.length > 0 && selectedYear !== 'all' && !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears, selectedYear]);

  // Year-filtered Documents
  const yearFilteredDocs = useMemo(() => {
    if (selectedYear === 'all') return documents;
    return documents.filter((d) => d.created_at?.startsWith(selectedYear));
  }, [documents, selectedYear]);

  // Search and Type filtered Documents
  const filteredDocs = useMemo(() => {
    return yearFilteredDocs.filter((doc) => {
      const term = search.toLowerCase().trim();
      const matchesSearch =
        !term ||
        doc.file_name.toLowerCase().includes(term) ||
        doc.entity_type.toLowerCase().includes(term) ||
        doc.notes?.toLowerCase().includes(term);

      const matchesType = !typeFilter || doc.document_type === typeFilter;
      const matchesEntity = !entityFilter || doc.entity_type === entityFilter;

      return matchesSearch && matchesType && matchesEntity;
    });
  }, [yearFilteredDocs, search, typeFilter, entityFilter]);

  const handleResetFilters = () => {
    setSearch('');
    setTypeFilter('');
    setEntityFilter('');
  };

  const handleSoftVoid = async (reason: string) => {
    if (!voidDoc) return;
    try {
      await documentService.softVoidDocument(voidDoc.id, reason);
      setVoidDoc(null);
      await loadDocuments();
    } catch (err: any) {
      alert(`Error voiding document: ${err.message}`);
    }
  };

  // KPI Calculations across year-filtered documents
  const totalCount = yearFilteredDocs.length;
  const activeCount = yearFilteredDocs.filter((d) => !d.is_void).length;
  const voidedCount = yearFilteredDocs.filter((d) => d.is_void).length;
  const totalSizeBytes = yearFilteredDocs.reduce((sum, d) => sum + (Number(d.file_size) || 0), 0);

  // Document Type options for Combobox
  const docTypeOptions = [
    { value: 'weighing_sheet', label: 'Weighing Sheet' },
    { value: 'invoice', label: 'Supplier Invoice' },
    { value: 'delivery_receipt', label: 'Delivery Receipt' },
    { value: 'settlement_pdf', label: 'Settlement PDF' },
    { value: 'lab_result', label: 'Lab Result' },
    { value: 'photo', label: 'Field Photo' },
    { value: 'general', label: 'General File' },
  ];

  // Entity Type options for Combobox
  const entityTypeOptions = [
    { value: 'production_cycle', label: 'Production Cycle' },
    { value: 'feed_receipts', label: 'Feed Receipt' },
    { value: 'feed_transfers', label: 'Feed Transfer' },
    { value: 'pig_sales', label: 'Pig Sale / Scale' },
    { value: 'settlements', label: 'Grower Settlement' },
    { value: 'expenses', label: 'Health / Farm Expense' },
  ];

  return (
    <div className="space-y-5 max-w-7xl mx-auto pb-12">
      {/* 1. Standardized Page Header */}
      <PageHeader
        title={selectedYear === 'all' ? 'Document Repository — All Years' : `Document Repository — ${selectedYear}`}
        subtitle="Private audit attachments stored in vsb-attachments Supabase bucket."
        icon={FolderLock}
        helpScreenKey="documents"
        actions={
          <>
            <ActionTooltip content="Reload uploaded audit attachments and storage usage.">
              <Button
                variant="outline"
                size="sm"
                onClick={loadDocuments}
                className="text-xs h-8 sm:h-9 text-slate-700 hover:bg-slate-50 gap-1.5"
              >
                <RefreshCw className={`w-3.5 h-3.5 text-slate-500 ${loading ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
            </ActionTooltip>

            {isOperations && (
              <>
                <ActionTooltip content="Take or select a live camera photo for veterinary or gate pass proof.">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowCameraModal(true)}
                    className="text-xs h-8 sm:h-9 gap-1.5 border-slate-300 text-slate-700 hover:bg-slate-50"
                  >
                    <Camera className="w-3.5 h-3.5 text-slate-600" />
                    <span>Capture Photo</span>
                  </Button>
                </ActionTooltip>

                <ActionTooltip content="Upload physical weighing sheets, delivery receipts, or formal settlement PDFs.">
                  <Button
                    size="sm"
                    onClick={() => setShowUploadModal(!showUploadModal)}
                    className="text-xs h-8 sm:h-9 bg-emerald-700 hover:bg-emerald-600 text-white font-semibold gap-1.5 shadow-xs"
                  >
                    <UploadCloud className="w-3.5 h-3.5 text-emerald-200" />
                    <span>{showUploadModal ? 'Close Uploader' : 'Upload Document'}</span>
                  </Button>
                </ActionTooltip>
              </>
            )}
          </>
        }
        yearSelector={
          <YearSelector
            availableYears={availableYears}
            selectedYear={selectedYear}
            onYearChange={setSelectedYear}
          />
        }
      />

      {/* 2. Interactive Uploader Section (Expandable) */}
      {isOperations && showUploadModal && (
        <Card className="bg-slate-50 border-emerald-300 shadow-sm animate-in fade-in duration-150">
          <CardContent className="p-4 sm:p-5">
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <UploadCloud className="w-4 h-4 text-emerald-700" /> Upload File Attachment
            </h3>
            <DocumentUploader
              entityType="production_cycle"
              entityId="00000000-0000-0000-0000-000000000000"
              defaultDocumentType="weighing_sheet"
              onUploadSuccess={() => {
                loadDocuments();
              }}
            />
          </CardContent>
        </Card>
      )}

      {/* 3. Standardized KPI Summary Cards (4 Cards) */}
      <KpiCardGroup columns={4}>
        <KpiCard
          title="Total Documents"
          value={`${totalCount.toLocaleString()} files`}
          subtitle={selectedYear === 'all' ? 'All historical uploads' : `${totalCount} in ${selectedYear}`}
          icon={FileText}
          variant="default"
        />
        <KpiCard
          title="Active Attachments"
          value={`${activeCount.toLocaleString()} active`}
          subtitle="Non-voided verified records"
          icon={CheckCircle}
          variant="emerald"
        />
        <KpiCard
          title="Storage Footprint"
          value={formatFileSize(totalSizeBytes)}
          subtitle="Private bucket volume"
          icon={HardDrive}
          variant="default"
        />
        <KpiCard
          title="Archived / Voided"
          value={`${voidedCount.toLocaleString()} voided`}
          subtitle="Audit compliance preserved"
          icon={Archive}
          variant={voidedCount > 0 ? 'rose' : 'default'}
        />
      </KpiCardGroup>

      {/* 4. Standardized FilterBar */}
      <FilterBar
        searchTerm={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search file name, notes, entity..."
        onReset={handleResetFilters}
        showReset={Boolean(search || typeFilter || entityFilter)}
      >
        {/* Document Classification Combobox */}
        <div className="w-full sm:w-56">
          <Combobox
            options={docTypeOptions}
            value={typeFilter}
            onChange={setTypeFilter}
            allowAll={true}
            allLabel="All Classifications"
            placeholder="Filter Classification..."
            searchPlaceholder="Search type..."
          />
        </div>

        {/* Linked Entity Type Combobox */}
        <div className="w-full sm:w-56">
          <Combobox
            options={entityTypeOptions}
            value={entityFilter}
            onChange={setEntityFilter}
            allowAll={true}
            allLabel="All Entity Types"
            placeholder="Filter Entity Type..."
            searchPlaceholder="Search entity..."
          />
        </div>
      </FilterBar>

      {/* 5. Documents Table & Mobile View */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-400 text-xs font-medium">
            Loading document repository records...
          </div>
        ) : filteredDocs.length === 0 ? (
          <div className="p-12 text-center text-slate-500 text-xs">
            No documents found matching the filter criteria.
          </div>
        ) : (
          <>
            {/* Desktop Table View (md:table) */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse font-sans">
                <thead className="bg-slate-100 text-slate-700 font-semibold border-b border-slate-200">
                  <tr>
                    <th className="py-2.5 px-3">File Name</th>
                    <th className="py-2.5 px-3">Classification</th>
                    <th className="py-2.5 px-3">Linked Entity</th>
                    <th className="py-2.5 px-3 whitespace-nowrap">File Size</th>
                    <th className="py-2.5 px-3 whitespace-nowrap">Upload Date</th>
                    <th className="py-2.5 px-3 text-center">Status</th>
                    <th className="py-2.5 px-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {filteredDocs.map((doc) => (
                    <tr key={doc.id} className="hover:bg-slate-50 transition-colors">
                      <td className="py-2.5 px-3 font-medium text-slate-900 flex items-center gap-2">
                        <FileText className="w-4 h-4 text-emerald-700 flex-shrink-0" />
                        <span className="truncate max-w-xs">{doc.file_name}</span>
                      </td>
                      <td className="py-2.5 px-3">
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-slate-100 text-slate-700">
                          {doc.document_type.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 font-mono text-slate-600">
                        {doc.entity_type.replace(/_/g, ' ')}
                      </td>
                      <td className="py-2.5 px-3 font-mono text-slate-600 whitespace-nowrap">
                        {formatFileSize(doc.file_size)}
                      </td>
                      <td className="py-2.5 px-3 text-slate-500 font-mono whitespace-nowrap">
                        {formatDate(doc.created_at)}
                      </td>
                      <td className="py-2.5 px-3 text-center whitespace-nowrap">
                        {doc.is_void ? (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-800 line-through">
                            VOIDED
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
                            Active
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 px-3 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1.5">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setPreviewDoc(doc)}
                            className="h-7 text-xs px-2 text-slate-700 hover:bg-slate-50 border-slate-300"
                          >
                            <Eye className="w-3.5 h-3.5 mr-1 text-slate-500" /> View
                          </Button>
                          {!doc.is_void && isOperations && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setVoidDoc(doc)}
                              className="h-7 text-xs px-2 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                              title="Soft-void document"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Responsive Cards Layout (md:hidden) */}
            <div className="md:hidden divide-y divide-slate-200">
              {filteredDocs.map((doc) => (
                <div key={doc.id} className="p-3.5 space-y-2 hover:bg-slate-50">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-mono text-slate-500">{formatDate(doc.created_at)}</span>
                    {doc.is_void ? (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-800">
                        VOIDED
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
                        Active
                      </span>
                    )}
                  </div>

                  <div className="flex items-start gap-2">
                    <FileText className="w-4 h-4 text-emerald-700 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-bold text-slate-900 break-all">{doc.file_name}</p>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        {doc.document_type.replace(/_/g, ' ')} • {formatFileSize(doc.file_size)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-1">
                    <span className="text-[11px] font-mono text-slate-600">
                      Entity: {doc.entity_type.replace(/_/g, ' ')}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setPreviewDoc(doc)}
                        className="h-7 text-xs px-2"
                      >
                        <Eye className="w-3.5 h-3.5 mr-1" /> View
                      </Button>
                      {!doc.is_void && isOperations && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setVoidDoc(doc)}
                          className="h-7 text-xs px-2 text-rose-600 hover:bg-rose-50"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Modals */}
      {isOperations && (
        <PhotoCaptureModal
          isOpen={showCameraModal}
          onClose={() => setShowCameraModal(false)}
          entityType="production_cycle"
          entityId="00000000-0000-0000-0000-000000000000"
          defaultDocumentType="weighing_sheet"
          onPhotoCaptured={() => loadDocuments()}
        />
      )}

      <DocumentPreviewModal
        isOpen={previewDoc !== null}
        onClose={() => setPreviewDoc(null)}
        document={previewDoc}
      />

      <SoftVoidConfirmModal
        isOpen={voidDoc !== null}
        onClose={() => setVoidDoc(null)}
        onConfirm={handleSoftVoid}
        recordIdentifier={voidDoc?.file_name || 'Document'}
      />
    </div>
  );
};
